import { useState } from 'react';
import {
  Box,
  Divider as MUIDivider,
  makeStyles,
  withStyles
} from '@material-ui/core';
import {
  AIProductMatchBadge,
  SButton,
  SCheckbox,
  SETVIImage,
  SHighlightText,
  SRadio,
  SText
} from '@setvi/shared/components';
import Card from 'componentsV2/cards/grid/card';
import { Customer } from 'Services/Mongo/Types';
import { TextColor } from '@setvi/shared/enums';
import {
  ProductDetails,
  ProductGridItem,
  ProductPriceType,
  Product as ProductType
} from '@setvi/shared/interfaces';
import { getProductStatus } from 'componentsV2/product/status';
import { ProductLabel, PromotionBadge } from 'componentsV2';
import { MatchIndicator } from 'pages/auto-rfq/components/product-card/components';
import { Stock } from '../../../stock';
import { PriceButton } from '../../../price-button';

import { AIProductMatchTitle } from '../..';
import { FullPrice, ListPrice } from './components';
import { FullContent } from './components/price/full/components';

const Divider = withStyles(({ spacing }) => ({
  root: {
    margin: spacing(2, 0)
  }
}))(MUIDivider);

const useStyles = makeStyles(() => ({
  wrapper: {
    borderRadius: 12,
    width: 289
    // height: 339
  },
  img: {
    width: '100%',
    height: '100%',
    aspectRatio: '1.34/1',
    objectFit: 'scale-down',
    objectPosition: 'center'
  },
  topRightIcon: {
    position: 'absolute',
    top: 10,
    right: 10
  },
  topLeftIcon: {
    position: 'absolute',
    top: 10,
    left: 10
  }
}));

interface ProductProp {
  product: ProductType;
  priceType: ProductPriceType;
  gridConfig: ProductGridItem;
  selectedCustomer?: Customer;
  checkboxTooltip?: string;
  searchQuery?: string;
  checked: boolean;
  productPricesLoading?: boolean;
  stock: ProductDetails['stock'];
  aiSearchActive: boolean;
  stockRefresh?: boolean;
  type?: 'none' | 'checkbox' | 'radio';

  onProductClick?: (product: ProductType) => void;
  checkboxDisabled?: (product: ProductType) => boolean;
  onPromotionClick?: (product: ProductType) => void;
  onGetProductPrice?: (product: ProductType) => Promise<void>;
  onStockClick?: (product: ProductType) => void;
  handleCheck: (isChecked: boolean, product: ProductType[]) => void;
  updateProduct?: (
    product: ProductType,
    newValues: Partial<ProductType>
  ) => void;
}

export const leadTimeFontSize = 'sm';

export const Product = ({
  product,
  priceType,
  selectedCustomer,
  gridConfig,
  stock,
  aiSearchActive,
  productPricesLoading,
  checkboxTooltip,
  searchQuery = '',
  checked,
  stockRefresh,
  type,
  onProductClick,
  onPromotionClick,
  onGetProductPrice,
  updateProduct,
  onStockClick,
  handleCheck,
  checkboxDisabled
}: ProductProp) => {
  const [singlePriceLoading, setSinglePriceLoading] = useState(false);
  const classes = useStyles();

  return (
    <Card
      item={product}
      onCardClick={onProductClick}
      className={classes.wrapper}>
      <Box m={2}>
        <Box position="relative">
          <SETVIImage
            alt={product.name}
            src={
              product.imageUrl ||
              product.images?.[0]?.path ||
              '/images/product-placeholder.svg'
            }
            className={classes.img}
          />
          <Box className={classes.topRightIcon}>
            {product.promotions?.length > 0 && (
              <PromotionBadge onClick={() => onPromotionClick(product)} />
            )}
          </Box>
          <Box
            className={classes.topLeftIcon}
            onClick={e => e.stopPropagation()}>
            {type === 'checkbox' && (
              <SCheckbox
                checked={checked}
                disabled={checkboxDisabled?.(product)}
                tooltip={checkboxTooltip}
                onChange={e => handleCheck(e.target.checked, [product])}
              />
            )}
            {type === 'radio' && (
              <SRadio
                name="radio-button-product"
                onChange={e => {
                  handleCheck(e.target.checked, [product]);
                }}
                checked={checked}
                disabled={checkboxDisabled?.(product)}
                tooltip={checkboxTooltip}
              />
            )}
          </Box>
        </Box>
        <Box m={2} mb={0}>
          <Box height={40} display="flex" alignItems="center">
            <SHighlightText
              maxLines={2}
              value={product.name}
              size="sm"
              searchQuery={searchQuery}
            />
          </Box>
          <Box display="flex">
            <SHighlightText
              size="sm"
              interactive
              fontColor={TextColor.Grey}
              value={`SKU: ${product.sku}`}
              searchQuery={searchQuery}
              tooltipContent={<>{product.sku}</>}
            />
            <Box ml={1} minWidth="max-content">
              <SText
                fontColor={TextColor.Grey}
                size="sm"
                title={`(${getProductStatus(product)})`}
              />
            </Box>
          </Box>
          <Divider />

          <Box
            mt={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Box display="grid" width="100%">
              <Box
                height={20}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gridGap="4px">
                <SHighlightText
                  size="sm"
                  value={`MFG: ${product?.vendor?.name || 'Not Available'}`}
                  searchQuery={searchQuery}
                  style={{ marginRight: '10px' }}
                />

                <Box display="flex" gridGap="6px">
                  {product.privateLabel && <ProductLabel />}
                  {product?.vendorHierarchy > 0 && (
                    <ProductLabel type="strategic-vendor" />
                  )}
                </Box>
              </Box>

              {priceType !== ProductPriceType.List &&
                (gridConfig?.manufacturerSku || stock) && (
                  <Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      height={19}
                      gridGap="6px">
                      {gridConfig?.manufacturerSku && (
                        <SText
                          style={{ maxWidth: '170px' }}
                          size="sm"
                          color="textSecondary"
                          title={product?.manufacturerSku || '-'}
                        />
                      )}
                      {stock && (
                        <Stock
                          variant="body2"
                          inventory={product.inventory}
                          isDropship={product.dropship}
                          isStockProduct={product?.isStockProduct}
                          {...(stockRefresh
                            ? {
                                stockRefresh: {
                                  id: product.sku,
                                  onStockRefresh: newInventory =>
                                    updateProduct(product, {
                                      inventory: newInventory
                                    })
                                }
                              }
                            : {
                                onClick: () => onStockClick(product)
                              })}
                          selectedCustomer={selectedCustomer}
                        />
                      )}
                    </Box>
                    {gridConfig?.manufacturerLeadTime && (
                      <SText
                        title={`Lead time: ${product?.custom?.leadTime ? `${product?.custom?.leadTime} Days` : '-'}`}
                        size={leadTimeFontSize}
                      />
                    )}
                  </Box>
                )}
            </Box>
          </Box>
          {priceType === ProductPriceType.Full &&
            !!gridConfig?.pricing.length && (
              <>
                <Divider />
                <FullPrice
                  product={product}
                  loading={productPricesLoading}
                  pricing={gridConfig?.pricing}
                />
              </>
            )}
          {priceType === ProductPriceType.Get && !!selectedCustomer && (
            <>
              <Divider />
              <Box display="flex" justifyContent="center" height={40}>
                <PriceButton
                  price={product.price}
                  onGetPrice={() => onGetProductPrice(product)}
                />
              </Box>
            </>
          )}
          {priceType === ProductPriceType.GetFull && !!selectedCustomer && (
            <>
              <Divider />
              <Box display="flex" justifyContent="center" height={44}>
                {product.price ? (
                  <FullContent
                    fontSize="sm"
                    product={product}
                    pricing={gridConfig?.pricing}
                  />
                ) : (
                  <SButton
                    loading={singlePriceLoading}
                    onClick={async e => {
                      e.stopPropagation();
                      setSinglePriceLoading(true);
                      await onGetProductPrice(product);
                      setSinglePriceLoading(false);
                    }}
                    style={{ width: 'max-content' }}
                    variant="outlined">
                    Get Price
                  </SButton>
                )}
              </Box>
            </>
          )}

          {priceType === ProductPriceType.List && (
            <>
              <Divider />
              <ListPrice product={product} />
            </>
          )}

          {aiSearchActive && (
            <Box display="flex" flexDirection="column">
              <Divider />
              <Box display="flex" alignItems="center" gridGap="4px" mb={2}>
                <AIProductMatchBadge value={product?.percentage} />
                {product?.percentage > 0 && (
                  <MatchIndicator percentage={product?.percentage} />
                )}
              </Box>
              <Box height={47} display="flex" alignItems="center">
                <SText
                  size="xs"
                  fontColor={TextColor.SlateGrey}
                  maxLines={3}
                  prefix={
                    <b
                      style={{
                        color: '#20262D',
                        marginRight: 4
                      }}>
                      {AIProductMatchTitle}:
                    </b>
                  }
                  title={product?.reasoning}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};
